/**
 * Term Sheet Context DTO (Issue #1306)
 *
 * Strictly whitelisted rendering context for Handlebars-based term sheet
 * template merging. Every field here is the COMPLETE set of data that the
 * PDF renderer exposes to tenant-authored template content.
 *
 * SECURITY — CRITICAL READ:
 *   This shape is a firewall between internal platform state and tenant-
 *   authored text. Adding a field here means tenants can reference it in
 *   their templates. NEVER add:
 *     - Environment variables or secrets
 *     - Internal-only Business fields (e.g. Business.internalFlags)
 *     - Cross-tenant data
 *     - Platform configuration
 *     - Raw Prisma records (they may carry hidden columns)
 *
 *   Fields must be simple primitives or plain nested objects matching the
 *   shape declared here. No methods, no prototype-attached properties, no
 *   references back to request/response objects.
 *
 * Consumed by:
 *   - 11-report-generation-service HandlebarsEngineService.render()
 *   - 34-crm-service POST /internal/termsheets/validate-template
 *     (uses TermSheetContextSkeletonSchema for static analysis)
 *   - Issue #4 admin editor (uses CONTEXT_SHAPE for autocomplete)
 */

import { z } from 'zod';

// ============================================================================
// ADDRESS
// ============================================================================

export const TermSheetContextAddressSchema = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
});

export type TermSheetContextAddress = z.infer<typeof TermSheetContextAddressSchema>;

// ============================================================================
// BORROWER / CO-BORROWER / GUARANTOR
// ============================================================================

export const TermSheetContextBorrowerSchema = z.object({
  legalName: z.string(),
  dbaName: z.string().optional(),
  ein: z.string().optional(),
  address: TermSheetContextAddressSchema,
  email: z.string().email(),
  phone: z.string().optional(),
});

export type TermSheetContextBorrower = z.infer<typeof TermSheetContextBorrowerSchema>;

/**
 * Lightweight borrower/guarantor reference used for co-borrowers and guarantors
 * — always a list of people attached to the deal.
 */
export const TermSheetContextPartyRefSchema = z.object({
  legalName: z.string(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  ownershipPercentage: z.number().min(0).max(100).optional(),
});

export type TermSheetContextPartyRef = z.infer<typeof TermSheetContextPartyRefSchema>;

// ============================================================================
// PROPERTY
// ============================================================================

export const TermSheetContextPropertySchema = z.object({
  type: z.string(),
  address: TermSheetContextAddressSchema,
  purchasePrice: z.number().optional(),
  arv: z.number().optional(),
  rehabBudget: z.number().optional(),
});

export type TermSheetContextProperty = z.infer<typeof TermSheetContextPropertySchema>;

// ============================================================================
// LOAN
// ============================================================================

export const TermSheetContextLoanSchema = z.object({
  amount: z.number(),
  /** Decimal, e.g. 0.0725 = 7.25% */
  interestRate: z.number(),
  termMonths: z.number().int(),
  originationPoints: z.number(),
  /** ISO 8601 date string */
  maturityDate: z.string(),
  category: z.string(),
});

export type TermSheetContextLoan = z.infer<typeof TermSheetContextLoanSchema>;

// ============================================================================
// CALCULATED
// ============================================================================

export const TermSheetContextCalculatedSchema = z.object({
  ltv: z.number(),
  ltc: z.number(),
  monthlyPayment: z.number(),
  originationFee: z.number(),
  totalInterest: z.number(),
});

export type TermSheetContextCalculated = z.infer<typeof TermSheetContextCalculatedSchema>;

// ============================================================================
// LENDER
// ============================================================================

/**
 * SECURITY: Only tenant-branded, tenant-visible fields are exposed here.
 * The lender object is populated from Business branding only — never from
 * Business.internalFlags, subscription tier, billing state, or any
 * non-branding field.
 */
export const TermSheetContextLenderSchema = z.object({
  companyName: z.string(),
  logoUrl: z.string().optional(),
  contactEmail: z.string().email().optional(),
  contactPhone: z.string().optional(),
});

export type TermSheetContextLender = z.infer<typeof TermSheetContextLenderSchema>;

// ============================================================================
// DATES
// ============================================================================

export const TermSheetContextDatesSchema = z.object({
  /** ISO 8601 — today's date when the PDF was rendered */
  today: z.string(),
  /** ISO 8601 — today + template.validityDays */
  validUntil: z.string(),
});

export type TermSheetContextDates = z.infer<typeof TermSheetContextDatesSchema>;

// ============================================================================
// ROOT CONTEXT
// ============================================================================

/**
 * Whitelisted Handlebars rendering context. This is the ONLY data template
 * authors can reference.
 */
export const TermSheetContextSchema = z.object({
  borrower: TermSheetContextBorrowerSchema,
  coBorrowers: z.array(TermSheetContextPartyRefSchema),
  guarantors: z.array(TermSheetContextPartyRefSchema),
  property: TermSheetContextPropertySchema,
  loan: TermSheetContextLoanSchema,
  calculated: TermSheetContextCalculatedSchema,
  lender: TermSheetContextLenderSchema,
  dates: TermSheetContextDatesSchema,
});

export type TermSheetContext = z.infer<typeof TermSheetContextSchema>;

// ============================================================================
// CONTEXT PATH ENUMERATION (for autocomplete/linting — Issue #4)
// ============================================================================

/**
 * All valid top-level paths that template authors can reference.
 * Used by the admin editor's variable autocomplete and by the validation
 * API's "unknown_var" lint check.
 */
export const TERM_SHEET_CONTEXT_PATHS = [
  // borrower
  'borrower.legalName',
  'borrower.dbaName',
  'borrower.ein',
  'borrower.address.street',
  'borrower.address.city',
  'borrower.address.state',
  'borrower.address.zip',
  'borrower.email',
  'borrower.phone',
  // coBorrowers (array — use with {{#each}})
  'coBorrowers',
  // guarantors (array — use with {{#each}})
  'guarantors',
  // property
  'property.type',
  'property.address.street',
  'property.address.city',
  'property.address.state',
  'property.address.zip',
  'property.purchasePrice',
  'property.arv',
  'property.rehabBudget',
  // loan
  'loan.amount',
  'loan.interestRate',
  'loan.termMonths',
  'loan.originationPoints',
  'loan.maturityDate',
  'loan.category',
  // calculated
  'calculated.ltv',
  'calculated.ltc',
  'calculated.monthlyPayment',
  'calculated.originationFee',
  'calculated.totalInterest',
  // lender
  'lender.companyName',
  'lender.logoUrl',
  'lender.contactEmail',
  'lender.contactPhone',
  // dates
  'dates.today',
  'dates.validUntil',
] as const;

export type TermSheetContextPath = (typeof TERM_SHEET_CONTEXT_PATHS)[number];

/** Allowed helpers exposed by the render engine. Anything else is forbidden. */
export const TERM_SHEET_HELPER_NAMES = [
  'currency',
  'percent',
  'date',
  'uppercase',
  'lowercase',
  'defaultValue',
  'pluralize',
] as const;

export type TermSheetHelperName = (typeof TERM_SHEET_HELPER_NAMES)[number];

/**
 * Skeleton context with sentinel defaults. Used by the validation API to
 * lint templates before save — running a template against this skeleton
 * flushes out unknown-variable references without requiring a real Case.
 */
export function buildSkeletonTermSheetContext(): TermSheetContext {
  const today = new Date().toISOString();
  const validUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
  const address: TermSheetContextAddress = {
    street: '123 Sample St',
    city: 'Sampletown',
    state: 'CA',
    zip: '90001',
  };
  return {
    borrower: {
      legalName: 'Sample Borrower LLC',
      dbaName: 'Sample DBA',
      ein: '00-0000000',
      address,
      email: 'borrower@example.com',
      phone: '555-0100',
    },
    coBorrowers: [],
    guarantors: [],
    property: {
      type: 'Single Family Residence',
      address,
      purchasePrice: 0,
      arv: 0,
      rehabBudget: 0,
    },
    loan: {
      amount: 0,
      interestRate: 0,
      termMonths: 0,
      originationPoints: 0,
      maturityDate: today,
      category: 'bridge',
    },
    calculated: {
      ltv: 0,
      ltc: 0,
      monthlyPayment: 0,
      originationFee: 0,
      totalInterest: 0,
    },
    lender: {
      companyName: 'Sample Lender',
      logoUrl: undefined,
      contactEmail: 'lender@example.com',
      contactPhone: '555-0200',
    },
    dates: { today, validUntil },
  };
}

// ============================================================================
// VALIDATION API TYPES (Issue #1306 §4)
// ============================================================================

/** Category of validation error — drives inline editor markers. */
export const TermSheetValidationErrorKind = {
  PARSE: 'parse',
  UNKNOWN_VAR: 'unknown_var',
  FORBIDDEN_HELPER: 'forbidden_helper',
  RUNTIME: 'runtime',
} as const;

export type TermSheetValidationErrorKindType =
  (typeof TermSheetValidationErrorKind)[keyof typeof TermSheetValidationErrorKind];

export const TermSheetValidationErrorSchema = z.object({
  sectionIndex: z.number().int().min(0),
  sectionType: z.string(),
  line: z.number().int().min(1).optional(),
  column: z.number().int().min(1).optional(),
  message: z.string(),
  kind: z.enum(['parse', 'unknown_var', 'forbidden_helper', 'runtime']),
  /** The missing variable path (when kind=unknown_var) */
  missingPath: z.string().optional(),
  /** The forbidden helper name (when kind=forbidden_helper) */
  helperName: z.string().optional(),
});

export type TermSheetValidationError = z.infer<typeof TermSheetValidationErrorSchema>;

export const TermSheetValidationWarningSchema = z.object({
  sectionIndex: z.number().int().min(0),
  message: z.string(),
});

export type TermSheetValidationWarning = z.infer<typeof TermSheetValidationWarningSchema>;

/**
 * Input shape for POST /internal/termsheets/validate-template.
 * Caller passes EITHER templateId (load saved template) OR sections (ad-hoc).
 */
export const TermSheetValidateTemplateInputSchema = z
  .object({
    businessId: z.string().uuid(),
    templateId: z.string().uuid().optional(),
    sections: z
      .array(
        z.object({
          type: z.string(),
          title: z.string().optional(),
          content: z.string(),
        }),
      )
      .optional(),
  })
  .refine((v) => Boolean(v.templateId) !== Boolean(v.sections), {
    message: 'Provide exactly one of templateId or sections',
  });

export type TermSheetValidateTemplateInput = z.infer<typeof TermSheetValidateTemplateInputSchema>;

export const TermSheetValidateTemplateResultSchema = z.object({
  valid: z.boolean(),
  errors: z.array(TermSheetValidationErrorSchema),
  warnings: z.array(TermSheetValidationWarningSchema),
});

export type TermSheetValidateTemplateResult = z.infer<typeof TermSheetValidateTemplateResultSchema>;

// ============================================================================
// CANONICAL-KEY → NESTED-CONTEXT COMPAT LAYER (Issue #1531, Phase 2 PR B)
// ----------------------------------------------------------------------------
// Phase 2 keeps the existing nested `TermSheetContext` shape at the Handlebars
// render boundary so tenant-authored templates referencing `{{borrower.legalName}}`
// or `{{loan.amount}}` keep working unchanged. The runtime resolver in
// `11-report-generation-service/.../term-sheet-context.builder.ts` now reads
// `TemplateFieldMapping` rows, produces a flat dict keyed by canonical keys,
// and reconstructs the nested shape via `buildContextFromDict` below.
//
// Phase 5 cuts this compat layer: tenants migrate to `{{borrower_name}}`-style
// flat tokens, `buildContextFromDict` and `CANONICAL_KEY_TO_CONTEXT_PATH` are
// deleted, and the renderer consumes the flat dict directly. Until then the
// dict-of-keys is the *interchange format*; the nested object is the *render
// format*.
//
// See: docs/plans/20260429-PLAN-termsheet-phase-2-blueprint.md
//      ("TermSheetContext Reshape Decision" + risk R6 — exclusion rationale).
// ============================================================================

/**
 * Flat dictionary keyed by `LOAN_DATA_POINTS[].canonicalKey`. This is the
 * shape produced by the resolver (`resolveDict()` in the report-generation
 * service) before it is reshaped back into nested `TermSheetContext`.
 *
 * Values are stringified primitives — date strings stay ISO 8601, currency
 * stays a number, percentages stay a decimal (0.0725 = 7.25%). The Handlebars
 * helpers (`currency`, `percent`, `date`) format at render time.
 *
 * Missing keys are `undefined` — never `null`, never throw.
 */
export type TermSheetContextFlatDict = Record<string, string | number | undefined>;

/**
 * Mapping from canonical keys (`LOAN_DATA_POINTS[].canonicalKey`) to the
 * nested `TermSheetContext` path that receives the value. Hand-authored —
 * the registry has 36 keys but the context surfaces ~30, so the table is
 * intentionally lossy.
 *
 * SECURITY EXCLUSIONS — these registry keys are deliberately omitted because
 * they MUST NEVER appear on a term sheet, even if a tenant tries to bind a
 * DocuSeal field to them. The ContextRail UI filters out canonical keys not
 * present in this lookup, so the binding picker never offers them:
 *
 *   - `ssn`            — Social Security Number (registry key for intake/KYC
 *                        only; lender contracts must not echo SSN back to
 *                        the borrower).
 *   - `dob`            — Date of Birth (PII; intake/KYC only — same rationale
 *                        as SSN).
 *   - `credit_score`   — internal underwriting signal; surfacing it on a term
 *                        sheet would create a regulated written disclosure
 *                        (FCRA / Reg B implications).
 *
 * Other registry keys without obvious context paths (e.g. employment fields
 * on a DSCR loan where they're optional) are also excluded — those can be
 * added when a concrete template needs them.
 */
export const CANONICAL_KEY_TO_CONTEXT_PATH: Partial<Record<string, TermSheetContextPath>> = {
  // BORROWER — basic identity + contact (excluding SSN / DOB per security note above)
  borrower_name: 'borrower.legalName',
  email: 'borrower.email',
  phone: 'borrower.phone',
  // borrower address (single-line value lands on the street field; structured
  // sub-paths for city/state/zip are populated by the resolver when the
  // upstream registry value is a structured address object — see resolver).
  borrower_address: 'borrower.address.street',

  // PROPERTY
  property_type: 'property.type',
  property_address: 'property.address.street',
  purchase_price: 'property.purchasePrice',
  after_repair_value: 'property.arv',
  renovation_budget: 'property.rehabBudget',

  // LEGAL — entity name maps onto borrower.legalName when there's no separate
  // borrower person; entity_type/state_of_formation/EIN are not on the
  // current context shape (Phase 5 may add them when tenants ask).
  entity_name: 'borrower.legalName',
  ein: 'borrower.ein',

  // SECURITY EXCLUSIONS — explicitly omitted (do NOT add):
  //   ssn, dob, credit_score
  //
  // Other registry keys NOT yet surfaced on the context shape (omitted —
  // not a security exclusion, just out of scope for Phase 2):
  //   citizenship_status, employer_name, employment_position, years_employed,
  //   annual_income, monthly_income, monthly_debt, total_assets,
  //   total_liabilities, dti_ratio, account_balance, total_deposits,
  //   current_value, rental_income, occupancy_rate, noi, entity_type,
  //   state_of_formation
};

/**
 * Reconstruct the nested `TermSheetContext` shape from a flat canonical-key
 * dict. Pure function — no side effects, no I/O. Missing keys produce
 * `undefined` at the leaf (never throws).
 *
 * The skeleton context is used as the base so the returned object always has
 * every required nested object instantiated (e.g. `borrower.address` is
 * always present, even if every leaf is undefined). This matches the contract
 * the Handlebars renderer expects — nested traversals like
 * `{{borrower.address.street}}` never blow up on a missing parent object.
 */
export function buildContextFromDict(dict: TermSheetContextFlatDict): TermSheetContext {
  const ctx = buildSkeletonTermSheetContext();

  // Walk every entry in the lookup table; for each canonical key with a value
  // in the dict, splice that value into the nested path. Keys absent from the
  // dict leave the skeleton's sentinel value in place.
  for (const [canonicalKey, contextPath] of Object.entries(CANONICAL_KEY_TO_CONTEXT_PATH) as Array<
    [string, TermSheetContextPath]
  >) {
    const value = dict[canonicalKey];
    if (value === undefined) continue;
    setNestedPath(ctx, contextPath, value);
  }

  return ctx;
}

/**
 * Sets a value at a dotted path on the nested `TermSheetContext`. Internal
 * helper for `buildContextFromDict` — assumes the path is a known
 * `TermSheetContextPath` (the lookup table only emits valid paths) and that
 * every parent object along the path already exists on the skeleton.
 */
function setNestedPath(ctx: TermSheetContext, path: TermSheetContextPath, value: string | number): void {
  const segments = path.split('.');
  // Walk to the parent of the leaf. The skeleton guarantees every parent
  // exists, so each `current[segment]` is safe to dereference. The path is
  // type-narrowed by `TermSheetContextPath` upstream, but walking a nested
  // struct by string path is inherently dynamic.
  // biome-ignore lint/suspicious/noExplicitAny: see comment above
  let current: any = ctx;
  for (let i = 0; i < segments.length - 1; i++) {
    current = current[segments[i] as string];
  }
  const leaf = segments[segments.length - 1] as string;
  current[leaf] = value;
}
